import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import PartImage from "../stock/PartImage";
import { Fragment } from "react/jsx-runtime";
import classNames from "../../utils/classNames";
import LoadingWheel from "../shared/LoadingWheel";
import { EditAddress } from "../stock/shipping/EditAddress";
import useShipping from "../../data/useShipping";
import { useEffect, useState } from "react";
import { IShippingAddress, IShippingDimensions, IShippingLabelCreateRequest, ShippingLabelType } from "../../models/Shipping";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faXmark } from "@fortawesome/free-solid-svg-icons";
import { EditDimensions } from "../stock/shipping/EditDimensions";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import ShippingLabelRadio from "../stock/shipping/ShippingLabelRadio";
import { Button, RadioGroup } from "@headlessui/react";
import useShippingProviders from "../../data/useShippingProviders";
import { FedexShipping } from "../stock/FedexShipping";
import { ProoviaShipping } from "../stock/shipping/ProoviaShipping";
import ShippingProviders from "../stock/shipping/ShippingProviders";
import { info } from "console";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { useForm } from "react-hook-form";
import { set } from "lodash";
import useShippingBulk from "../../data/useShippingBulk";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useSinglePart from "../../data/useSinglePart";
import { useQueryClient } from "@tanstack/react-query";
import useShippingPolicies from "../../data/useShippingPolicies";
import ShippingPolicyCell from "./ShippingPolicyCell";
import LoadingSkeleton from "../shared/LoadingSkeleton";
import LoadingImage from "../shared/LoadingImage";

// #region helpers
export const resolveAddress = (part: IPart, shippingInfo: any) => {
  const contactAddress = shippingInfo.data?.data.contactAddress;

  return {
    addressLine1: part.addressLine1 ?? contactAddress?.addressLine1!,
    addressLine2: part.addressLine2 ?? contactAddress?.addressLine2!,
    city: part.city ?? contactAddress?.city!,
    county: part.county ?? contactAddress?.county!,
    postcode: part.postcode ?? contactAddress?.postalCode!,
    stateOrProvince: part.stateOrProvince ?? contactAddress?.stateOrProvince!,
    fullName: part.fullName ?? shippingInfo.data?.data.fullName!,
  };
};

export const resolveDimensions = (part: IPart, defaultPartCategory: any) => {
  const defaultDimensions = defaultPartCategory.data?.data;

  return {
    weight: part.weight ?? defaultDimensions?.weight!,
    height: part.height ?? defaultDimensions?.height!,
    length: part.length ?? defaultDimensions?.length!,
    width: part.width ?? defaultDimensions?.width!,
  };
};

const base64ToBlob = (b64: string, mime: string) => {
  const byteChars = atob(b64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mime });
};
// #endregion

// #region Address Cell
function AddressCell({ part, setValue, setPart }: { part: IPart, setValue: (name: string, value: any) => void, setPart: any }) {
  const queryClient = useQueryClient();
  const { shippingInfo } = useShipping(part);

  const [address, setAddress] = useState<IShippingAddress>();
  const [addressLoading, setAddressLoading] = useState(true);
  const [isAddressEditing, setIsAddressEditing] = useState(false);

  useEffect(() => {
    if (!shippingInfo.isLoading && shippingInfo.data) {
      setAddress(resolveAddress(part, shippingInfo));
      setAddressLoading(false);

      var address = { ...resolveAddress(part, shippingInfo) };

      setValue(`address.${part.id}`, address); 
    }
  }, [shippingInfo.isLoading, shippingInfo.data, part]);
  

  return (
    <div className="relative">
      {
        addressLoading ? (
          <LoadingSkeleton 
            width="w-48"
            lines={7}
          />
        ) : isAddressEditing ? (
          <EditAddress
            address={address!}
            partId={part.id}
            onEditSuccess={(result) => {
              setAddress(result);
              setIsAddressEditing(false);
              queryClient.invalidateQueries(["parts", part.id]);
              setPart((prevPart: IPart) => ({ ...prevPart, ...result }));
            }}
          />
        ) : (
          <>
            <p>{address?.fullName}</p>
            <p>{address?.addressLine1}</p>
            <p>{address?.addressLine2}</p>
            <p>{address?.city}</p>
            <p>{address?.county}</p>
            <p>{address?.stateOrProvince}</p>
            <p>{address?.postcode}</p>
          </>
        )
      }
      <FontAwesomeIcon
        className="rounded-full ml-1 cursor-pointer hover:ring-2 hover:ring-blue-500 p-1 absolute bottom-0 right-0"
        icon={isAddressEditing ? faXmark : faPencil}
        onClick={() => setIsAddressEditing(!isAddressEditing)}
      />
    </div>
  )

}
// #endregion 

// #region Dimensions Cell
function DimensionsCell({ part, setValue, setPart }: { part: IPart, setValue: (name: string, value: any) => void, setPart: any }) {
  const queryClient = useQueryClient();
  const { defaultPartCategory } = usePartDefaultCategories({ part });

  const [dimensions, setDimensions] = useState<IShippingDimensions>();
  const [dimensionsLoading, setDimensionsLoading] = useState(true);
  const [isDimensionsEditing, setIsDimensionsEditing] = useState(false);

  useEffect(() => {
    if (!defaultPartCategory.isLoading && defaultPartCategory.data) {
      setDimensions(resolveDimensions(part, defaultPartCategory));
      setDimensionsLoading(false);

      // find request by part id
      let request = { ...resolveDimensions(part, defaultPartCategory) };

      setValue(`dimensions.${part.id}`, request);
    }

    if (!part.categoryId) {
      setDimensionsLoading(false);
    }
  }, [defaultPartCategory.isLoading, defaultPartCategory.data, part]);

  return (
    <div className="relative">
      {dimensionsLoading ? (
        <div className="grid grid-cols-2 gap-4">
          <LoadingSkeleton lines={1} />
          <LoadingSkeleton lines={1} />
          <LoadingSkeleton lines={1} />
          <LoadingSkeleton lines={1} />
        </div>
      ) : isDimensionsEditing ? (
        <EditDimensions
          dimensions={dimensions!}
          partId={part.id}
          categoryId={part.categoryId ?? defaultPartCategory.data?.data.id}
          onEditSuccess={() => { 
            setIsDimensionsEditing(false);
            queryClient.invalidateQueries(["parts", part.id]);
            setPart((prevPart: IPart) => ({ ...prevPart, ...dimensions! }));
          }}
          cols="grid-cols-2"
          maxColSpan="col-span-2"
        />
      ) : (
        <div className="grid grid-cols-2 gap-2 text-center divide-x">
          <p>
            <span className="text-gray-400">Width:</span>{" "}
            {dimensions?.width}
            cm
          </p>
          <p>
            <span className="text-gray-400">Height:</span>{" "}
            {dimensions?.height}cm
          </p>
          <p>
            <span className="text-gray-400">Length:</span>{" "}
            {dimensions?.length}cm
          </p>
          <p>
            <span className="text-gray-400">Weight:</span>{" "}
            {dimensions?.weight}kg
          </p>
        </div>
      )}
      <FontAwesomeIcon
        className="rounded-full ml-1 cursor-pointer hover:ring-2 hover:ring-blue-500 p-1 float-right"
        icon={isDimensionsEditing ? faXmark : faPencil}
        onClick={() => setIsDimensionsEditing(!isDimensionsEditing)}
      />
    </div>
  )
}
// #endregion

// #region Shipping Provider Cell
function ShippingProviderCell({ part, shippingProvider, setShippingProvider, setShippingService, shippingLabelType, setShippingLabelType }: { part: IPart, shippingProvider: number, setShippingProvider: (value: number) => void, setShippingService: (value: string|undefined) => void, shippingLabelType: ShippingLabelType, setShippingLabelType: (value: ShippingLabelType) => void }) {
  const { shippingInfo } = useShipping(part);
  const { shippingPolicies } = useShippingPolicies();
  const { shippingProviders: providers } = useShippingProviders();
  const { defaultPartCategory } = usePartDefaultCategories({ part });

  const shippingPolicy = shippingPolicies.data?.data.find(
    policy => policy.id === part.shippingPolicyId
  );

  const [shippingProviders, setShippingProviders] = useState([
    { value: 0, label: "FedEx" }
  ]);

  const [addressLoading, setAddressLoading] = useState(true);
  const [dimensionsLoading, setDimensionsLoading] = useState(true);

  useEffect(() => {
    if (!shippingInfo.isLoading && shippingInfo.data) 
      setAddressLoading(false);
  }, [shippingInfo.isLoading, shippingInfo.data]);

  useEffect(() => {
    if (!defaultPartCategory.isLoading && defaultPartCategory.data) 
      setDimensionsLoading(false);

    if (!part.categoryId) setDimensionsLoading(false);
  }, [defaultPartCategory.isLoading, defaultPartCategory.data]);

  useEffect(() => {
    // TODO: Make this all dynamic based on available providers once the API supports voila
    if (!providers.data || providers.isLoading) return;

    const availableProviders = providers.data.data.map(provider => {
      const settings = provider.requiredSettings ?? {};

      // any set
      const anySet = Object.values(settings).some((val) => val?.trim() !== "");

      if (!anySet) return null;

      return {
        value: 0,
        label: provider.providerName,
      };
    }).filter(provider => provider !== null);

    let limitedProviders = [];

    let fedexProvider = availableProviders.find(p => p?.label === "FedEx");
    if (fedexProvider) {
      fedexProvider.value = 0; // Ensure FedEx is always 0
      limitedProviders.push(fedexProvider);
      setShippingProvider(0); // Default to FedEx
    } else setShippingProvider(1); // Default to Proovia if FedEx is not available

    let prooviaProvider = availableProviders.find(p => p?.label === "Proovia");
    if (prooviaProvider) {
      prooviaProvider.value = 1; // Ensure Proovia is always 1
      limitedProviders.push(prooviaProvider);
    }

    setShippingProviders(limitedProviders);

  }, [providers.data, providers.isLoading]);

  useEffect(() => {
    setShippingLabelType(shippingLabelType);
  }, [shippingLabelType]);

  useEffect(() => {
    var courier = shippingPolicy?.courier ?? '';

    switch (courier) {
      case "FedEx":
        setShippingProvider(0);
        break;
      case "Proovia":
        setShippingProvider(1);
        break;
      case "Parcelforce":
        // setShippingProvider(1);
        break;
      case "Dx":
        // setShippingProvider(2);
        break;
      case "RoyalMail":
        // setShippingProvider(3);
        break;
    }

  }, [shippingPolicy])

  const handleChangeProvider = (value: number) => {
    setShippingService(undefined);
    setShippingProvider(value);
  };

  return <>
    {addressLoading || dimensionsLoading ? (
      <div className="gap-3">
        <LoadingSkeleton width="w-full" lines={0} title />
        <LoadingSkeleton width="w-full" lines={0} title />
        <div className="grid grid-cols-2 gap-3">
          <LoadingImage height="h-12" />
          <LoadingImage height="h-12" />
        </div>
      </div>
    ) : (
      !part.shippingLabelDocumentId && (
        <div>
          <div className="w-full">
            <RadioGroup
              value={shippingProvider}
              onChange={handleChangeProvider}
              className="my-4"
            >
              <RadioGroup.Label className="sr-only">
                Choose which shipper to use
              </RadioGroup.Label>
              <div className={`grid grid-cols-1 gap-3`}>
                {shippingProviders.map((type) => (
                  <RadioGroup.Option
                    key={`${type.value}`}
                    value={type.value}
                    className={({ active, checked }) =>
                      classNames(
                        active
                          ? "ring-2 ring-offset-2 ring-primary-500"
                          : "",
                        checked
                          ? "bg-primary-600 border-transparent text-white hover:bg-primary-700"
                          : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                        "cursor-pointer focus:outline-none border rounded-md py-1.5 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                      )
                    }
                  >
                    <RadioGroup.Label as="span">
                      {type.label}
                    </RadioGroup.Label>
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>
            <div className="max-w-[160px] m-auto">
              <ShippingLabelRadio type={shippingLabelType} setType={setShippingLabelType} />
            </div>
          </div>
       
        </div>
      )
    )}
  </>;
}
// #endregion

// #region Shipping Row
function ShippingRow({ 
  part: tablePart, 
  setValue,
  provider,
  labelType,
  service,
  getValues
}: { 
  part: IPart, 
  setValue: (name: string, value: any) => void,
  provider?: number,
  labelType?: ShippingLabelType,
  service?: string,
  getValues: (name: string) => any
}) {
  const { singlePart } = useSinglePart(tablePart.id);
  const [part, setPart] = useState<IPart>(tablePart);

  useEffect(() => {
    if (!singlePart.isLoading && singlePart.data) {
      setPart(singlePart.data.data);
    }
  }, [singlePart.isLoading, singlePart.data]);

  return (
    <tr key={part.id} className={classNames("hover:bg-gray-100")}>
      <td className="px-3 py-3 border text-sm w-24">
        <PartImage part={part} />
      </td>
      <td className="px-3 py-3 border text-sm">
        <AddressCell part={part} setPart={setPart} setValue={setValue} />
      </td>
      <td className="px-3 py-3 border text-sm">
        <DimensionsCell part={part} setPart={setPart} setValue={setValue} />
      </td>
      <td className="px-3 py-3 border text-sm">
        <ShippingPolicyCell part={part} />
      </td>
      <td className="px-3 py-3 border text-sm max-w-[180px]">
        <ShippingProviderCell 
          part={part} 
          shippingProvider={provider || 0}
          setShippingProvider={(value: number) => {
            setValue(`provider.${part.id}`, value);
            setValue(`service.${part.id}`, undefined);
          }}
          setShippingService={(value: string | undefined) => setValue(`service.${part.id}`, value)}
          shippingLabelType={labelType || ShippingLabelType.S16}
          setShippingLabelType={(value: ShippingLabelType) => setValue(`labelType.${part.id}`, value)}
        />
      </td>
        <td className="px-3 py-3 border text-sm min-w-[250px] max-w-[300px]">
        <ShippingProviders 
          part={part} 
          provider={getValues(`provider.${part.id}`) || 0}
          shippingService={service}
          setShippingService={(value: string | undefined) => setValue(`service.${part.id}`, value)}
          setProoviaItemId={(value: number) => setValue(`itemId.${part.id}`, value)}
          setProoviaCollection={(value: string) => setValue(`collectionDate.${part.id}`, value)}
          setProoviaDelivery={(value: string) => setValue(`deliveryDate.${part.id}`, value)}
        />
      </td>
    </tr>
  );
}
// #endregion

// #region Modal
export default function CreateShippingLabelsModal({ isOpen, onClose, parts: rows } : { isOpen: boolean, onClose: () => void, parts: IPart[]}) {
  const { saveHandlers } = useDefaultCRUDHandlers("Shipping Label");
  const { shippingLabels } = useShippingBulk();
  const { register, handleSubmit, setValue, reset, getValues, watch } = useForm();
  const [loading, setLoading] = useState(false);

  const [parts, setParts] = useState<IPart[]>([]);

  const headers  = [
    "Image",
    "Shipping Address",
    "Dimensions & Weight",
    "Shipping Policy",
    "Shipping Provider",
    "Service"
  ];

  useEffect(() => {
    // Reset state when modal is closed
    if (!isOpen) {
      reset();
      setParts([]);
    }

    setParts(rows);
  }, [rows, isOpen]);

  const handleCreateLabels = () => {
    var requests: IShippingLabelCreateRequest[] = [];

    var data = getValues();

    console.log("Form data", data);
    console.log("Creating labels for ", parts);

    parts.forEach(part => {
      let address    = part.id in data[`address`]    ? data[`address`][part.id]    : undefined;
      let dimensions = part.id in data[`dimensions`] ? data[`dimensions`][part.id] : undefined;
      // if (!address || !dimensions) return;

      let request: IShippingLabelCreateRequest = {
        partDataId: part.id,
        service   : part.id in data[`service`]   ? data[`service`][part.id]   : null,
        labelType : part.id in data[`labelType`] ? data[`labelType`][part.id] : ShippingLabelType.S16,
        ...address,
        ...dimensions
      };

      if (`provider.${part.id}` in data && data[`provider.${part.id}`] === 1) { // Proovia
        if (`itemId.${part.id}`         in data) request.prooviaItemId  = data[`itemId.${part.id}`];
        if (`collectionDate.${part.id}` in data) request.collectionDate = data[`collectionDate.${part.id}`];
        if (`deliveryDate.${part.id}`   in data) request.deliveryDate   = data[`deliveryDate.${part.id}`];
      }

      requests.push(request);
    });

    console.log(requests);

    setLoading(true);

    // your mutate call
    shippingLabels.mutate(requests, {
      onSuccess: async (resp: Response) => {
        saveHandlers.onSuccess();

        // Parse JSON: { files: [{ fileName, contentType, data(Base64) }], failed: [...] }
        const payload = await resp.json();

        // Download each file
        for (const f of payload.files as Array<{fileName: string; contentType: string; data: string;}>) {
          const blob = base64ToBlob(f.data, f.contentType);
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = f.fileName || "label.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }

        // Optional: show failures (if any)
        if (payload.failed?.length) {
          console.warn(`Some shipments failed: ${payload.failed.join(", ")}`);
        }

        onClose();
      },
      onError: async (err: any) => {
        saveHandlers.onError();
        console.warn(err);
      },
      onSettled: () => setLoading(false),
    });
  }
  
  return(
    <Modal open={isOpen} setOpen={onClose} width="max-w-[95vw] h-[95vh]">
      <form className="p-4" onSubmit={handleSubmit(handleCreateLabels)}>
        <p className="text-lg font-bold mb-2">Create Shipping Labels</p>

        <ButtonInput
          label="Create Labels"
          onClick={handleCreateLabels}
          classes={"!px-4 !py-2 !bg-green-600 hover:!bg-green-700 mb-4"}
          isLoading={loading}
        />

        <div className="overflow-x-auto max-h-[75vh]">
          <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
            <thead>
              <tr className="bg-primary-50 text-gray-800 sticky top-0 z-10">
                {headers.map((header, i) => (
                  <th
                    key={i}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                    > 
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parts.map((part, i) => {
                var provider  = watch(`provider.${part.id}`);
                var labelType = watch(`labelType.${part.id}`);
                var service   = watch(`service.${part.id}`);

                return (<Fragment key={i}>
                  <ShippingRow 
                    part={part} 
                    setValue={setValue} 
                    provider={provider}
                    labelType={labelType}
                    service={service}
                    getValues={getValues}
                  />
                </Fragment>)
              })}
            </tbody>
          </table>
        </div>
      </form>
    </Modal>
  )
}
// #endregion
